import { useState, useEffect } from 'react';
import { fetchCurrencies } from '../api/endpointsBackend';

/**
 * MI-298 stock view: `rates` maps currency name → USD value of 1 unit **today**,
 * resolved by the backend (peg-aware) as `usd_per_unit` on GET /currencies.
 * `null` for a currency means unconvertible (custom without peg / no history).
 *
 * Cross-rate: rate(src → target) = rates[src] / rates[target]. One payload
 * converts to any target, so switching the display currency needs no refetch.
 *
 * `currencies` is the same payload unreduced. Balances are converted client-side
 * (there is no converted-total endpoint for stocks), so when one can't convert,
 * the definition is what says *why* — `type` and `manual_rate` are exactly what
 * `unconvertibleReason` needs. Returned from the same fetch rather than a second
 * hook, so the endpoint is still hit once.
 *
 * `sources` maps a currency to the rates its value came from (MI-329): its
 * `rate_source`, which the "≈" tooltip on a converted balance names.
 *
 * `rates` is `null` while loading and `{}` on error.
 */
export function useUsdRates() {
  const [state, setState] = useState({ rates: null, sources: {}, currencies: [] });

  useEffect(() => {
    let cancelled = false;
    fetchCurrencies(true)
      .then((list) => {
        if (cancelled) return;
        setState({
          rates: Object.fromEntries((list || []).map((c) => [c.name, c.usd_per_unit])),
          sources: Object.fromEntries((list || []).map((c) => [c.name, c.rate_source])),
          currencies: list || [],
        });
      })
      .catch(() => { if (!cancelled) setState({ rates: {}, sources: {}, currencies: [] }); });
    return () => { cancelled = true; };
  }, []);

  return state;
}
